/**
 * Structured listing/filter attributes for the Apartments collection.
 * Drives /apartments filter facets and room-type card metadata.
 */

export const apartmentFurnishingIds = ['fullyFurnishedEquipped', 'fullyFurnished'] as const

export type ApartmentFurnishingId = (typeof apartmentFurnishingIds)[number]

export const apartmentFurnishingSelectOptions = [
  { label: 'Fully furnished & equipped', value: 'fullyFurnishedEquipped' },
  { label: 'Fully furnished', value: 'fullyFurnished' },
] as const

export const roomTypesPreferenceIds = [
  'fully-furnished-equipped',
  'fully-furnished',
  'accessibility',
  'balcony',
  'patio',
] as const

export type RoomTypesPreferenceId = (typeof roomTypesPreferenceIds)[number]

export type ApartmentListingFilters = {
  furnishing?: ApartmentFurnishingId | null
  hasBalcony?: boolean | null
  hasPatio?: boolean | null
  isAccessible?: boolean | null
}

const FURNISHING_LABELS = Object.fromEntries(
  apartmentFurnishingSelectOptions.map((option) => [option.value, option.label]),
) as Record<ApartmentFurnishingId, string>

export function isApartmentFurnishingId(value: string): value is ApartmentFurnishingId {
  return (apartmentFurnishingIds as readonly string[]).includes(value)
}

export function isRoomTypesPreferenceId(value: string): value is RoomTypesPreferenceId {
  return (roomTypesPreferenceIds as readonly string[]).includes(value)
}

export function getApartmentFurnishingLabel(furnishing: ApartmentFurnishingId): string {
  return FURNISHING_LABELS[furnishing] ?? furnishing
}

/** Maps CMS furnishing enum to the public preference filter id. */
export function furnishingToPreferenceId(
  furnishing: ApartmentFurnishingId | null | undefined,
): RoomTypesPreferenceId | null {
  if (furnishing === 'fullyFurnishedEquipped') return 'fully-furnished-equipped'
  if (furnishing === 'fullyFurnished') return 'fully-furnished'
  return null
}

export function resolveApartmentListingFilters(
  filters: ApartmentListingFilters | null | undefined,
): Required<ApartmentListingFilters> {
  return {
    furnishing: filters?.furnishing ?? null,
    hasBalcony: Boolean(filters?.hasBalcony),
    hasPatio: Boolean(filters?.hasPatio),
    isAccessible: Boolean(filters?.isAccessible),
  }
}

const FULLY_FURNISHED_EQUIPPED_PATTERN =
  /fully\s*furnished\s*(?:&|and)\s*equipped|furnished\s*&\s*equipped|voll\s*möbliert\s*(?:&|und)\s*ausgestattet|voll\s*ausgestattet|fully\s*equipped|\bequipped\b|\bausgestattet\b/i

const FULLY_FURNISHED_PATTERN =
  /fully\s*furnished|\bmöbliert\b|voll\s*möbliert|\bfurnished\b(?!\s*(?:&|and)\s*equipped)/i

const BALCONY_PATTERN = /\bbalcony\b|\bbalkon\b/i
const PATIO_PATTERN = /\bpatio\b|\bterrasse\b|\bterrace\b/i
const ACCESSIBILITY_PATTERN =
  /\baccessible\b|\baccessibility\b|\bbarrier[-\s]?free\b|\bwheelchair\b|\bbarrierefrei\b/i

/** Infer listing filters from legacy title and amenity title strings (import/backfill). */
export function inferApartmentListingFiltersFromText(args: {
  amenityTitles?: readonly string[]
  title?: string | null
}): ApartmentListingFilters {
  const texts = [args.title?.trim() ?? '', ...(args.amenityTitles ?? [])].filter(Boolean)

  let furnishing: ApartmentFurnishingId | null = null
  let hasBalcony = false
  let hasPatio = false
  let isAccessible = false

  for (const text of texts) {
    if (!furnishing && FULLY_FURNISHED_EQUIPPED_PATTERN.test(text)) {
      furnishing = 'fullyFurnishedEquipped'
    } else if (!furnishing && FULLY_FURNISHED_PATTERN.test(text)) {
      furnishing = 'fullyFurnished'
    }

    if (BALCONY_PATTERN.test(text)) hasBalcony = true
    if (PATIO_PATTERN.test(text)) hasPatio = true
    if (ACCESSIBILITY_PATTERN.test(text)) isAccessible = true
  }

  return {
    furnishing,
    hasBalcony,
    hasPatio,
    isAccessible,
  }
}

export function apartmentMatchesPreferenceId(
  filters: ApartmentListingFilters | null | undefined,
  preferenceId: RoomTypesPreferenceId,
): boolean {
  const resolved = resolveApartmentListingFilters(filters)

  switch (preferenceId) {
    case 'fully-furnished-equipped':
      return resolved.furnishing === 'fullyFurnishedEquipped'
    case 'fully-furnished':
      return resolved.furnishing === 'fullyFurnished'
    case 'accessibility':
      return resolved.isAccessible
    case 'balcony':
      return resolved.hasBalcony
    case 'patio':
      return resolved.hasPatio
    default:
      return false
  }
}
